import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useGetBannerTypeQuery } from 'widgets/content/ui/banner-type/api/BannerTypeController';
import { LanguageType, TabPanelTypeProps } from 'shared/types';
import { useGetActivitiesQuery } from 'features/getActivities/api/getActivitiesApi';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { SelectChangeEvent } from '@mui/material/Select';
import { toast } from 'react-toastify';
import { useUploadFileMutation } from 'entities/banner/api/uploadFileApi';
import { DefaultValueDrawerBannerType } from 'entities/banner/types';

type BannerCardProps = {
  title: string;
  form: UseFormReturn<Partial<DefaultValueDrawerBannerType>>;
  onSubmit: (data: Partial<DefaultValueDrawerBannerType>) => SubmitHandler<Partial<DefaultValueDrawerBannerType>>;
};

type Logic = {
  [key: string]: boolean;
};

export const BannerCard = ({ title, form, onSubmit }: BannerCardProps) => {
  const PromoTypeBanner = ['b2b', 'b2cr', 'b2c'];
  const { control, setValue, handleSubmit, watch } = form;

  const { data: activitiesList, isSuccess: isSuccessActivitiesList } = useGetActivitiesQuery({
    app_type: 'admin',
    page: 1,
    size: 10,
    search: '',
    activity_type_ids: [],
  });
  const { data: bannerTypes, isSuccess: isSuccessBannerTypes } = useGetBannerTypeQuery(null);
  const { data: languagesList, isSuccess: isSuccessLanguagesList } = useGetLanguagesQuery(null);
  const [uploadFileRequest] = useUploadFileMutation();

  const [tabValue, setTabValue] = useState(0);
  const [fileData, setFileData] = useState<{ [key: string]: { name: string; file: File | null } }>({});

  useEffect(() => {
    if (isSuccessLanguagesList) {
      const initialFileData: { [key: string]: { name: string; file: File | null } } = {};

      languagesList.forEach((language) => {
        initialFileData[language.id.toString()] = { name: '', file: null };
      });

      setFileData(initialFileData);
    }
  }, [isSuccessLanguagesList, languagesList]);

  const TabPanel = (props: TabPanelTypeProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ marginTop: '16px' }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const disableOptions = (variantSelect: string) => {
    const logic: Logic = {
      app_type:
        watch?.('type_id') === 2 || watch?.('type_id') === 3 || watch?.('type_id') === 4 || watch?.('type_id') === null,
      activity_id: watch?.('type_id') === 1 || watch?.('type_id') === null,
    };

    return logic[variantSelect];
  };

  const selectOnChange = (e: SelectChangeEvent<number | null>) => {
    const value = e.target.value as number;
    setValue?.('type_id', value);
    if (value === 1) {
      setValue?.('activity_id', null);
    } else if (value === 2 || value === 3 || value === 4) {
      setValue?.('app_type', null);
    }
  };

  const handleUploadClick = async (index: number) => {
    const inputFile = fileData[index + 1];
    const formData = new FormData();
    formData.append('file', inputFile.file as File);

    uploadFileRequest(formData)
      .then((response) => {
        if ('data' in response) {
          setValue?.(`contents.${index}.image`, response.data.file_path as string);
          toast.success('Файл успешно сохранен', {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error('Произошла ошибка при сохранении файла', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error('Произошла ошибка при сохранении файла', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>, languageId: string) => {
    if (e.target.files != null && e.target.files[0] != null) {
      const file = e.target.files[0];
      const updatedFileData = { ...fileData };
      updatedFileData[languageId] = { name: e.target.files[0].name, file };

      setFileData(updatedFileData);
    }
  };

  return (
    <>
      {isSuccessBannerTypes && (
        <Controller
          control={control}
          name="type_id"
          render={({ field: { onChange, ...restField } }) => (
            <FormControl
              fullWidth
              sx={{ marginBottom: '24px' }}
            >
              <InputLabel id="demo-simple-select-label">Тип баннера</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Тип баннера"
                {...restField}
                onChange={(e) => selectOnChange(e)}
              >
                {bannerTypes.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}
      <Controller
        control={control}
        name="app_type"
        render={({ field }) => (
          <FormControl
            fullWidth
            sx={{ marginBottom: '24px' }}
          >
            <InputLabel id="demo-simple-select-label">Направление</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Направление"
              disabled={disableOptions('app_type')}
              {...field}
            >
              {PromoTypeBanner.map((item) => (
                <MenuItem value={item}>{item.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      {isSuccessActivitiesList && (
        <Controller
          control={control}
          name="activity_id"
          render={({ field }) => (
            <FormControl
              fullWidth
              sx={{ marginBottom: '24px' }}
            >
              <InputLabel id="select-label-type">Акции</InputLabel>
              <Select
                labelId="select-label-type"
                id="select-label-type"
                label="Акции"
                disabled={disableOptions('activity_id')}
                {...field}
              >
                {activitiesList.items.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.name.ru}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}
      <Controller
        control={control}
        name="priority"
        render={({ field }) => (
          <FormControl
            fullWidth
            sx={{ marginBottom: '24px' }}
          >
            <InputLabel id="demo-simple-select-label">Приоритет</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Приоритет"
              {...field}
            >
              <MenuItem value={1}>Низкий</MenuItem>
              <MenuItem value={2}>Средний</MenuItem>
              <MenuItem value={3}>Высокий</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="status"
        render={({ field }) => (
          <FormControl
            fullWidth
            sx={{ marginBottom: '24px' }}
          >
            <InputLabel id="demo-simple-select-label">Статус</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Статус"
              {...field}
            >
              <MenuItem value={1}>Активен</MenuItem>
              <MenuItem value={2}>Не активен</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      {isSuccessLanguagesList && (
        <div className="content">
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleChangeTabValue}
                aria-label="basic tabs example"
              >
                {languagesList.map((language: LanguageType, index: number) => (
                  <Tab
                    key={language.id}
                    label={language.codename}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>
            {languagesList.map((language: LanguageType, index: number) => (
              <TabPanel
                key={language.id}
                value={tabValue}
                index={index}
              >
                <Controller
                  control={control}
                  name={`contents.${index}.title`}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Название баннера"
                      size="small"
                      sx={{ marginBottom: '24px' }}
                      {...field}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`contents.${index}.description`}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      multiline
                      label="Описание баннера"
                      rows={4}
                      sx={{ marginBottom: '24px' }}
                      {...field}
                    />
                  )}
                />
                <div>
                  <input
                    id={`my-file-input-${language.id}`}
                    style={{ color: 'transparent' }}
                    type="file"
                    onChange={(e) => handleFileChange(e, language.id.toString())}
                  />
                  <div>{fileData[language.id]?.name || 'Файл не выбран'}</div>
                </div>
                <Button onClick={() => handleUploadClick(index)}>Сохранить</Button>
              </TabPanel>
            ))}
          </Box>
        </div>
      )}
      <Button
        variant="contained"
        sx={{ textTransform: 'none' }}
        onClick={handleSubmit(onSubmit)}
      >
        {title}
      </Button>
    </>
  );
};
